import React, { useEffect, useMemo, useState } from "react";
import { api, fmt } from "../../../services/erpApi";
import { StatCard, PageHeader, LoadingScreen, Modal, EmptyState } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import { Search, Plus, BookOpen, Banknote, Landmark, BarChart3 } from "lucide-react";

const PAGE_SIZE = 20;

const typeColor = (t) => {
  const map = {
    asset: { text: "#225f2b", bg: "#f0f7f0" },
    liability: { text: "#0f766e", bg: "#eef6f3" },
    equity: { text: "#b45309", bg: "#fffbeb" },
    income: { text: "#225f2b", bg: "#f0f7f0" },
    expense: { text: "#be123c", bg: "#fff1f2" },
  };
  return map[t] || { text: "#475569", bg: "#f1f5f9" };
};
const typeLabel = (t) => {
  const map = { asset: "Asset", liability: "Liability", equity: "Equity", income: "Income", expense: "Expense" };
  return map[t] || t || "—";
};

const AdminPanelAccountsPage = () => {
  const toast = useToast();
  const [accounts, setAccounts] = useState([]);
  const [types, setTypes] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(1);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", account_type: "", reconcile: false, parent_id: "", is_active: true });
  const [detail, setDetail] = useState(null);

  useEffect(() => setPage(1), [search, typeFilter]);

  const load = async () => {
    try {
      const rows = await api.accounts.list({ limit: 500 });
      setAccounts(rows || []);
    } catch { setAccounts([]); }
  };

  useEffect(() => {
    (async () => {
      await Promise.all([
        load(),
        api.accounts.types().catch(() => []),
      ]).then(([, t]) => setTypes(t || []));
      setLoaded(true);
    })();
  }, []);

  const safeTypes = types.length > 0 ? types : ["asset", "liability", "equity", "income", "expense"].map((c) => ({ code: c, name: c }));

  const stats = useMemo(() => {
    const byType = { asset: 0, liability: 0, equity: 0, income: 0, expense: 0 };
    accounts.forEach((a) => { if (byType[a.account_type] !== undefined) byType[a.account_type]++; });
    return { count: accounts.length, ...byType };
  }, [accounts]);

  const filtered = useMemo(() => {
    return accounts.filter((a) => {
      if (typeFilter !== "all" && a.account_type !== typeFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${a.name || ""} ${a.code || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [accounts, typeFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openDetail = async (acct) => {
    try {
      const d = await api.accounts.get(acct.id);
      setDetail(d || acct);
    } catch { setDetail(acct); }
  };

  const handleCreate = async () => {
    if (!form.code) { toast("Account code is required", "error"); return; }
    if (!form.name) { toast("Account name is required", "error"); return; }
    setCreating(true);
    try {
      const body = {
        code: form.code.trim(),
        name: form.name.trim(),
        account_type: form.account_type || undefined,
        reconcile: form.reconcile,
        parent_id: form.parent_id ? parseInt(form.parent_id) : undefined,
        is_active: form.is_active,
      };
      await api.accounts.create(body);
      toast("Account created", "success");
      setShowCreate(false);
      setForm({ code: "", name: "", account_type: "", reconcile: false, parent_id: "", is_active: true });
      await load();
    } catch (e) {
      toast(e.message || "Creation failed", "error");
    }
    setCreating(false);
  };

  const inputStyle = {
    width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
    fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box",
  };
  const labelStyle = { fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px", display: "block" };
  const FormField = ({ label, children }) => (
    <div style={{ marginBottom: "16px" }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );

  if (!loaded) return <LoadingScreen />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader
        title="Chart of Accounts"
        subtitle="Accounting plan — account types, codes, balances"
        actions={
          <button onClick={() => { setForm({ code: "", name: "", account_type: "", reconcile: false, parent_id: "", is_active: true }); setShowCreate(true); }} className="ess-btn ess-btn-primary">
            <Plus size={16} />
            New Account
          </button>
        }
      />

      <div className="ess-sg ess-sg4" style={{ marginBottom: "24px" }}>
        <StatCard label="Total Accounts" value={String(stats.count)} icon={BookOpen} trend="Chart size" />
        <StatCard label="Assets" value={String(stats.asset)} icon={Banknote} trend="Asset accounts" />
        <StatCard label="Liabilities" value={String(stats.liability)} icon={Landmark} trend="Liability accounts" />
        <StatCard label="Income / Expense" value={String(stats.income + stats.expense)} icon={BarChart3} trend={`${stats.income} income · ${stats.expense} expense`} />
      </div>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "24px", background: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e7e2" }}>
        <div style={{ flex: 1, minWidth: "200px", position: "relative" }}>
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94a3b8" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code or name…" style={{ ...inputStyle, padding: "9px 12px 9px 36px" }} />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ ...inputStyle, minWidth: "180px", cursor: "pointer" }}>
          <option value="all">All Types</option>
          {safeTypes.map((t) => <option key={t.code} value={t.code}>{typeLabel(t.code)}</option>)}
        </select>
      </div>

      <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
        {paginated.length === 0 ? (
          <EmptyState icon="📒" title="No accounts" subtitle="Accounts will appear once the chart of accounts is loaded" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                  <th className="ess-th" style={{ minWidth: "110px" }}>Code</th>
                  <th className="ess-th" style={{ minWidth: "200px" }}>Name</th>
                  <th className="ess-th" style={{ minWidth: "110px" }}>Type</th>
                  <th className="ess-th" style={{ textAlign: "center", minWidth: "90px" }}>Reconcile</th>
                  <th className="ess-th" style={{ textAlign: "center", minWidth: "80px" }}>Children</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "120px" }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((a, index) => (
                  <tr key={a.id} onClick={() => openDetail(a)} className="ess-tr" style={{ borderBottom: index < paginated.length - 1 ? "1px solid #f1f3f0" : "none", cursor: "pointer" }}>
                    <td className="ess-td" style={{ fontWeight: "600", color: "#225f2b", fontFamily: "monospace", whiteSpace: "nowrap" }}>{a.code || "—"}</td>
                    <td className="ess-td" style={{ fontWeight: "500", color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name || "—"}</td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                      <span style={{ background: typeColor(a.account_type).bg, color: typeColor(a.account_type).text, padding: "3px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "500", whiteSpace: "nowrap" }}>{typeLabel(a.account_type)}</span>
                    </td>
                    <td className="ess-td" style={{ textAlign: "center", color: a.reconcile ? "#225f2b" : "#94a3b8", fontWeight: "600", whiteSpace: "nowrap" }}>{a.reconcile ? "✓" : "—"}</td>
                    <td className="ess-td" style={{ textAlign: "center", color: "#64748b", whiteSpace: "nowrap" }}>{a.children_count ?? 0}</td>
                    <td className="ess-td" style={{ fontWeight: "600", color: (a.balance || 0) < 0 ? "#be123c" : "#1e293b", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(a.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #e2e7e2", background: "#fafbfa" }}>
          <span style={{ fontSize: "13px", color: "#64748b" }}>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div style={{ display: "flex", gap: "4px" }}>
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: page <= 1 ? "#f1f5f9" : "white", color: page <= 1 ? "#94a3b8" : "#334155", fontSize: "13px", fontWeight: "500", cursor: page <= 1 ? "not-allowed" : "pointer" }}>← Prev</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const start = Math.max(1, Math.min(page - 2, totalPages - 4));
              const p = start + i;
              if (p > totalPages) return null;
              return <button key={p} onClick={() => setPage(p)} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: p === page ? "#225f2b" : "white", color: p === page ? "white" : "#334155", fontSize: "13px", fontWeight: p === page ? "600" : "500", cursor: "pointer" }}>{p}</button>;
            })}
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: page >= totalPages ? "#f1f5f9" : "white", color: page >= totalPages ? "#94a3b8" : "#334155", fontSize: "13px", fontWeight: "500", cursor: page >= totalPages ? "not-allowed" : "pointer" }}>Next →</button>
          </div>
        </div>
      </div>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Account" size="md">
        <div>
          <FormField label="Code *">
            <input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="512000" style={inputStyle} />
          </FormField>
          <FormField label="Name *">
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Banque Société Générale" style={inputStyle} />
          </FormField>
          <FormField label="Type">
            <select value={form.account_type} onChange={(e) => setForm((f) => ({ ...f, account_type: e.target.value }))} style={inputStyle}>
              <option value="">Select type…</option>
              {safeTypes.map((t) => <option key={t.code} value={t.code}>{typeLabel(t.code)}</option>)}
            </select>
          </FormField>
          <FormField label="Parent Account">
            <select value={form.parent_id} onChange={(e) => setForm((f) => ({ ...f, parent_id: e.target.value }))} style={inputStyle}>
              <option value="">No parent…</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
            </select>
          </FormField>
          <FormField label="Reconcile">
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input type="checkbox" checked={form.reconcile} onChange={(e) => setForm((f) => ({ ...f, reconcile: e.target.checked }))} />
              <span style={{ fontSize: "13px", color: "#475569" }}>Allow reconciliation</span>
            </label>
          </FormField>
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: "20px" }}>
            <button onClick={() => setShowCreate(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button onClick={handleCreate} disabled={creating} className="ess-btn ess-btn-primary">
              {creating ? "Creating…" : "Create Account"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail ? `${detail.code} — ${detail.name}` : ""} size="md">
        {detail && (
          <div>
            <div style={{ display: "grid", gap: "12px", marginBottom: "16px" }} className="ess-rg-2">
              <div style={{ background: "#fafbfa", border: "1px solid #e2e7e2", borderRadius: "8px", padding: "14px", textAlign: "center" }}>
                <div style={{ fontSize: "20px", fontWeight: "700", color: "#1e293b" }}>{fmt.money(detail.balance)}</div>
                <div style={{ fontSize: "11px", color: "#64748b" }}>Balance</div>
              </div>
              <div style={{ background: "#fafbfa", border: "1px solid #e2e7e2", borderRadius: "8px", padding: "14px", textAlign: "center" }}>
                <div style={{ fontSize: "20px", fontWeight: "700", color: "#1e293b" }}>{detail.children?.length ?? detail.children_count ?? 0}</div>
                <div style={{ fontSize: "11px", color: "#64748b" }}>Child accounts</div>
              </div>
            </div>
            <div style={{ fontSize: "13px", color: "#475569", lineHeight: "1.8" }}>
              <p><strong>Type</strong> — {typeLabel(detail.account_type)}</p>
              <p><strong>Reconcile</strong> — {detail.reconcile ? "Yes" : "No"}</p>
              <p><strong>Active</strong> — {detail.is_active ? "Yes" : "No"}</p>
            </div>
            {detail.children?.length > 0 && (
              <div style={{ marginTop: "8px" }}>
                <p style={{ fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "8px" }}>Children</p>
                {detail.children.map((c) => (
                  <div key={c.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", background: "#fafbfa", border: "1px solid #eef1ee", borderRadius: "8px", marginBottom: "6px" }}>
                    <span style={{ fontSize: "13px", fontWeight: "500", color: "#1e293b" }}>{c.code} — {c.name}</span>
                    <span style={{ fontSize: "13px", fontWeight: "600", color: "#64748b" }}>{fmt.money(c.balance)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminPanelAccountsPage;